import { devLog, devError } from '../utils/devLogger';
// Comprehensive Audit Trail Service for Compliance
// Tracks all user actions, system changes, and data modifications

import { auth, db } from '../firebase';
import { collection, addDoc, serverTimestamp, query, where, orderBy, limit, getDocs, startAfter } from 'firebase/firestore';

class AuditTrailService {
  constructor() {
    this.pendingEntries = [];
    this.batchSize = 10;
    this.flushInterval = 5000; // 5 seconds
    this.startBatchProcessor();
  }

  // Core audit logging method
  async logAction(action, details = {}) {
    const auditEntry = {
      action,
      userId: auth.currentUser?.uid || 'system',
      userEmail: auth.currentUser?.email || 'system@npigovernance.co.za',
      userRole: auth.currentUser?.role || 'unknown',
      timestamp: new Date().toISOString(),
      ipAddress: await this.getClientIP(),
      userAgent: navigator.userAgent,
      sessionId: this.getSessionId(),
      location: window.location.pathname,
      details: {
        ...details,
        buildVersion: process.env.REACT_APP_VERSION || 'unknown',
        environment: process.env.REACT_APP_ENV || 'development'
      }
    };

    // Add to pending queue
    this.pendingEntries.push(auditEntry);

    // For critical actions, flush immediately
    if (this.isCriticalAction(action)) {
      await this.flushPendingEntries();
    }

    return auditEntry;
  }

  // User Authentication Actions
  async logLogin(method = 'email', success = true, additionalDetails = {}) {
    return await this.logAction('user.login', {
      method,
      success,
      loginTimestamp: new Date().toISOString(),
      ...additionalDetails
    });
  }

  async logLogout(additionalDetails = {}) {
    return await this.logAction('user.logout', {
      logoutTimestamp: new Date().toISOString(),
      sessionDuration: this.getSessionDuration(),
      ...additionalDetails
    });
  }

  async logFailedLogin(email, reason, additionalDetails = {}) {
    return await this.logAction('user.login.failed', {
      attemptedEmail: email,
      failureReason: reason,
      ...additionalDetails
    });
  }

  // User Management Actions
  async logUserCreated(userData) {
    return await this.logAction('user.created', {
      targetUserId: userData.uid,
      targetUserEmail: userData.email,
      targetUserRole: userData.role,
      createdFields: Object.keys(userData)
    });
  }

  async logUserUpdated(userId, changes) {
    return await this.logAction('user.updated', {
      targetUserId: userId,
      changedFields: Object.keys(changes),
      previousValues: changes.previousValues || {},
      newValues: changes.newValues || {}
    });
  }

  async logUserDeleted(userId, userEmail) {
    return await this.logAction('user.deleted', {
      targetUserId: userId,
      targetUserEmail: userEmail,
      deletionTimestamp: new Date().toISOString()
    });
  }

  async logRoleChanged(userId, oldRole, newRole) {
    return await this.logAction('user.role.changed', {
      targetUserId: userId,
      previousRole: oldRole,
      newRole: newRole,
      roleChangeTimestamp: new Date().toISOString()
    });
  }

  // Data Access Actions
  async logDataAccess(resourceType, resourceId, accessType = 'read') {
    return await this.logAction('data.access', {
      resourceType,
      resourceId,
      accessType,
      accessTimestamp: new Date().toISOString()
    });
  }

  async logDataExport(exportType, recordCount, format = 'csv') {
    return await this.logAction('data.export', {
      exportType,
      recordCount,
      format,
      exportTimestamp: new Date().toISOString()
    });
  }

  async logSensitiveDataAccess(dataType, recordIds = []) {
    return await this.logAction('data.sensitive.access', {
      dataType,
      recordIds: Array.isArray(recordIds) ? recordIds : [recordIds],
      accessTimestamp: new Date().toISOString()
    });
  }

  // Learning Management Actions
  async logModuleCreated(moduleData) {
    return await this.logAction('lms.module.created', {
      moduleId: moduleData.id,
      moduleName: moduleData.name,
      moduleType: moduleData.type,
      createdFields: Object.keys(moduleData)
    });
  }

  async logModuleUpdated(moduleId, changes) {
    return await this.logAction('lms.module.updated', {
      moduleId,
      changedFields: Object.keys(changes),
      updateTimestamp: new Date().toISOString()
    });
  }

  async logAssignmentSubmitted(assignmentId, submissionData) {
    return await this.logAction('lms.assignment.submitted', {
      assignmentId,
      submissionId: submissionData.id,
      submissionTimestamp: submissionData.submittedAt,
      fileCount: submissionData.files?.length || 0
    });
  }

  async logGradeAssigned(assignmentId, studentId, grade, gradedBy) {
    return await this.logAction('lms.grade.assigned', {
      assignmentId,
      studentId,
      grade,
      gradedBy,
      gradingTimestamp: new Date().toISOString()
    });
  }

  // Attendance Actions
  async logAttendanceMarked(sessionId, attendanceData) {
    return await this.logAction('attendance.marked', {
      sessionId,
      attendeeCount: attendanceData.length,
      presentCount: attendanceData.filter(a => a.status === 'present').length,
      absentCount: attendanceData.filter(a => a.status === 'absent').length,
      markingTimestamp: new Date().toISOString()
    });
  }

  async logAttendanceModified(sessionId, studentId, oldStatus, newStatus, reason) {
    return await this.logAction('attendance.modified', {
      sessionId,
      studentId,
      previousStatus: oldStatus,
      newStatus: newStatus,
      modificationReason: reason,
      modificationTimestamp: new Date().toISOString()
    });
  }

  // Integration Actions
  async logIntegrationAction(integration, action, success = true, details = {}) {
    return await this.logAction('integration.action', {
      integration, // 'teams', 'classroom', 'jotform', 'docusign'
      integrationAction: action,
      success,
      actionTimestamp: new Date().toISOString(),
      ...details
    });
  }

  async logDocumentSigned(documentId, signerEmail, documentType) {
    return await this.logAction('document.signed', {
      documentId,
      signerEmail,
      documentType,
      signingTimestamp: new Date().toISOString()
    });
  }

  async logFormSubmission(formId, formType, submissionData) {
    return await this.logAction('form.submitted', {
      formId,
      formType,
      submissionId: submissionData.id,
      fieldCount: Object.keys(submissionData.data || {}).length,
      submissionTimestamp: new Date().toISOString()
    });
  }

  // System Actions
  async logSystemConfiguration(configType, changes) {
    return await this.logAction('system.configuration', {
      configurationType: configType,
      changedSettings: Object.keys(changes),
      configurationTimestamp: new Date().toISOString()
    });
  }

  async logSystemError(errorType, errorMessage, errorDetails = {}) {
    return await this.logAction('system.error', {
      errorType,
      errorMessage,
      errorTimestamp: new Date().toISOString(),
      ...errorDetails
    });
  }

  async logBackupCreated(backupType, dataSize, backupLocation) {
    return await this.logAction('system.backup.created', {
      backupType,
      dataSize,
      backupLocation,
      backupTimestamp: new Date().toISOString()
    });
  }

  // Compliance-specific actions
  async logDataRetentionAction(action, recordType, recordCount, retentionPeriod) {
    return await this.logAction('compliance.data.retention', {
      retentionAction: action, // 'archived', 'deleted', 'extended'
      recordType,
      recordCount,
      retentionPeriod,
      actionTimestamp: new Date().toISOString()
    });
  }

  async logPrivacyRequest(requestType, dataSubject, requestDetails) {
    return await this.logAction('compliance.privacy.request', {
      requestType, // 'access', 'portability', 'deletion', 'rectification'
      dataSubjectEmail: dataSubject,
      requestTimestamp: new Date().toISOString(),
      ...requestDetails
    });
  }

  async logComplianceReport(reportType, generatedBy, recordCount) {
    return await this.logAction('compliance.report.generated', {
      reportType,
      generatedBy,
      recordCount,
      generationTimestamp: new Date().toISOString()
    });
  }

  // Query methods for audit reports
  async getAuditTrail(filters = {}) {
    try {
      let auditQuery = collection(db, 'audit_trail');

      // Apply filters
      if (filters.userId) {
        auditQuery = query(auditQuery, where('userId', '==', filters.userId));
      }

      if (filters.action) {
        auditQuery = query(auditQuery, where('action', '==', filters.action));
      }

      if (filters.startDate) {
        auditQuery = query(auditQuery, where('timestamp', '>=', filters.startDate));
      }

      if (filters.endDate) {
        auditQuery = query(auditQuery, where('timestamp', '<=', filters.endDate));
      }

      // Order by timestamp descending
      auditQuery = query(auditQuery, orderBy('timestamp', 'desc'));

      // Apply limit
      if (filters.limit) {
        auditQuery = query(auditQuery, limit(filters.limit));
      }

      const querySnapshot = await getDocs(auditQuery);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      devError('Error fetching audit trail:', error);
      throw error;
    }
  }

  async getUserActions(userId, startDate = null, endDate = null) {
    return await this.getAuditTrail({
      userId,
      startDate,
      endDate,
      limit: 1000
    });
  }

  async getSystemEvents(eventTypes = [], startDate = null, endDate = null) {
    const allEvents = await this.getAuditTrail({ startDate, endDate });

    if (eventTypes.length === 0) {
      return allEvents.filter(event => event.action.startsWith('system.'));
    }

    return allEvents.filter(event => eventTypes.includes(event.action));
  }

  async getComplianceReport(startDate, endDate) {
    const auditEntries = await this.getAuditTrail({ startDate, endDate });

    const report = {
      period: { startDate, endDate },
      totalActions: auditEntries.length,
      uniqueUsers: new Set(auditEntries.map(e => e.userId)).size,
      actionsByType: {},
      userActivity: {},
      criticalActions: [],
      dataAccessEvents: [],
      systemErrors: []
    };

    auditEntries.forEach(entry => {
      // Count actions by type
      report.actionsByType[entry.action] = (report.actionsByType[entry.action] || 0) + 1;

      // Count user activity
      if (!report.userActivity[entry.userId]) {
        report.userActivity[entry.userId] = {
          email: entry.userEmail,
          role: entry.userRole,
          actionCount: 0,
          lastActivity: entry.timestamp
        };
      }
      report.userActivity[entry.userId].actionCount++;

      // Collect critical actions
      if (this.isCriticalAction(entry.action)) {
        report.criticalActions.push(entry);
      }

      // Collect data access events
      if (entry.action.includes('data.')) {
        report.dataAccessEvents.push(entry);
      }

      // Collect system errors
      if (entry.action.includes('error')) {
        report.systemErrors.push(entry);
      }
    });

    return report;
  }

  // Utility methods
  isCriticalAction(action) {
    const criticalActions = [
      'user.deleted',
      'user.role.changed',
      'data.export',
      'data.sensitive.access',
      'system.configuration',
      'compliance.privacy.request',
      'system.backup.created'
    ];

    return criticalActions.some(critical => action.includes(critical));
  }

  async getClientIP() {
    try {
      // In production, you might want to use a service to get real client IP
      return 'client-ip-not-available';
    } catch {
      return 'unknown';
    }
  }

  getSessionId() {
    let sessionId = sessionStorage.getItem('audit_session_id');
    if (!sessionId) {
      sessionId = Date.now().toString(36) + Math.random().toString(36).substr(2);
      sessionStorage.setItem('audit_session_id', sessionId);
    }
    return sessionId;
  }

  getSessionDuration() {
    const sessionStart = sessionStorage.getItem('session_start_time');
    if (sessionStart) {
      return Date.now() - parseInt(sessionStart);
    }
    return null;
  }

  // Batch processing
  startBatchProcessor() {
    setInterval(async () => {
      if (this.pendingEntries.length > 0) {
        await this.flushPendingEntries();
      }
    }, this.flushInterval);
  }

  async flushPendingEntries() {
    if (this.pendingEntries.length === 0) return;

    const entriesToFlush = [...this.pendingEntries];
    this.pendingEntries = [];

    try {
      const promises = entriesToFlush.map(entry =>
        addDoc(collection(db, 'audit_trail'), {
          ...entry,
          createdAt: serverTimestamp()
        })
      );

      await Promise.all(promises);
      devLog(`✅ Flushed ${entriesToFlush.length} audit entries to database`);
    } catch (error) {
      devError('❌ Failed to flush audit entries:', error);
      // Re-add entries to queue for retry
      this.pendingEntries.unshift(...entriesToFlush);
    }
  }

  // Cleanup on page unload
  async shutdown() {
    await this.flushPendingEntries();
  }
}

// Create singleton instance
const auditTrailService = new AuditTrailService();

// Set session start time
if (!sessionStorage.getItem('session_start_time')) {
  sessionStorage.setItem('session_start_time', Date.now().toString());
}

// Ensure cleanup on page unload
window.addEventListener('beforeunload', () => {
  auditTrailService.shutdown();
});

export default auditTrailService;